const { CircularProgress, Box, Tooltip, Typography } = require("@mui/material");
const {
  useObjectDeleted,
  useCacheDiscardEventListener,
} = require("components/shared/apiCrudBuilder");
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import useCustomDataSource, { getCacheKey } from "./useCustomDataSource";
import { useCustomModel } from "components/context/CustomProvider";
import { useEffect } from "react";
import useDidValueChange from "components/hooks/useDidValueChanged";

const CustomModelProvider = ({
  id,
  children,
  modelName,
  render,
  renderError,
  onDeleted,
  renderLoading,
  renderPending,
}) => {
  const model = useCustomModel(modelName);
  const dataSource = useCustomDataSource(modelName);
  const isDeleted = useObjectDeleted([dataSource.key, `${id || ""}`]);

  const { data, error, isLoading, isSuccess, isError, isPending, refetch } =
    dataSource.useOne(id, { enabled: !!id && !isDeleted });

  model.fields.forEach((field) => {
    if (field.type === "primaryKeyRelatedField") {
      if (field.subModel) {
        useCacheDiscardEventListener(
          getCacheKey({ modelName: field.subModel }),
          ({ key, type }) => {
            if (type === "deleteOne" || type === "createOne") {
              refetch();
            }
          },
        );
      }
    }
  });

  useDidValueChange(isDeleted, "isDeleted");
  useDidValueChange(onDeleted, "onDeleted");

  useEffect(() => {
    if (isDeleted) onDeleted && onDeleted();
  }, [isDeleted, onDeleted]);

  if (isDeleted) {
    return <div>Object has been deleted</div>;
  }

  if (isError) {
    return renderError ? (
      renderError(error)
    ) : (
      <Tooltip title={<pre>{JSON.stringify(error, null, 2)}</pre>}>
        <Typography color="error">
          Error
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              verticalAlign: "text-bottom",
              marginLeft: "4px",
            }}
          >
            <InfoOutlineIcon sx={{ fontSize: "1.2em" }} />
          </span>
        </Typography>
      </Tooltip>
    );
  }

  if (isLoading) {
    return renderLoading ? (
      renderLoading()
    ) : (
      <Box
        display="flex"
        justifyContent="center"
        height="100%"
        alignItems="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isPending) {
    return renderPending ? (
      renderPending()
    ) : (
      <Box
        display="flex"
        justifyContent="center"
        height="100%"
        alignItems="center"
      >
        Pending...
      </Box>
    );
  }

  if (!isSuccess) {
    return <div>Status is weird .... ask your CTO.</div>;
  }

  return render
    ? render(data)
    : typeof children === "function"
    ? children(data, refetch)
    : children;
};

export default CustomModelProvider;
